import React, { useMemo } from 'react';

import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { SigningStatusBadge } from 'src/components/workflow/workflow-list/cells/SigningStatusBadge';
import {
  getIsSigningStatusState,
  SIGNING_STATUS_STATES,
} from 'src/components/workflow/workflow-list/signing-status';
import type { SigningStatusState } from 'src/components/workflow/workflow-list/signing-status';
import type { Apollo } from 'src/gql/generated';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';

const SIGNING_OPTIONS: Array<{ id: SigningStatusState; label: string }> = SIGNING_STATUS_STATES.map(
  state => ({
    id: state,
    label: state,
  })
);

function getIsWorkflowStateArray(value: unknown): value is Apollo.WorkflowState[] {
  return Array.isArray(value);
}

export function SigningStatusFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map(String) : []);
  }, [filterModel, field]);

  const handleToggle = (stateId: string) => {
    if (!getIsSigningStatusState(stateId)) return;

    const currentRaw = filterModel.items.find(item => item.field === field)?.value;
    const currentValues = getIsWorkflowStateArray(currentRaw) ? currentRaw : [];
    const isSelected = selectedIds.has(stateId);
    const newValues = isSelected
      ? currentValues.filter(value => value !== stateId)
      : [...currentValues, stateId];

    const updatedItems = filterModel.items.filter(item => item.field !== field);
    if (newValues.length > 0) {
      updatedItems.push({ field, operator, value: newValues });
    }
    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      options={SIGNING_OPTIONS}
      renderOption={option => <SigningStatusBadge state={option.id} />}
      selectedIds={selectedIds}
      onToggle={handleToggle}
    />
  );
}
